/**
 * SC-06 (AIVA-6810) — "rewrite this slide": read the slide, one AI call, then a confirm card.
 * Nothing is written until Apply rewrites; the card's decision is handled by `decideSlideRewrite`.
 */

import type { Dispatch, SetStateAction } from 'react';
import { emitToast } from '../../../integrations/tools/outlook/toast-bridge';
import type { RefreshTokenFn } from '../../shared/api/fetchWithRefresh';
import { logOfficeError } from '../../telemetry';
import { startCapability, type CapabilitySpan, type CapabilityTrigger } from '../../telemetry/capability';
import { NO_SLIDE_SELECTED_MESSAGE, SLIDE_UNREADABLE_MESSAGE } from '../../integrations/powerpoint/deckIntelligence/slideContent';
import { REPHRASE_COPY, rephraseAppliedMessage } from '../../integrations/powerpoint/const';
import { applySlideRewrites } from '../../integrations/powerpoint/features/rephrase-slide/services/applySlideRewrites';
import { buildRephrasePrompt } from '../../integrations/powerpoint/features/rephrase-slide/services/buildRephrasePrompt';
import { matchRephraseSlideIntent } from '../../integrations/powerpoint/features/rephrase-slide/services/matchRephraseSlideIntent';
import { parseRewrites } from '../../integrations/powerpoint/features/rephrase-slide/services/parseRewrites';
import { readRephraseTargets } from '../../integrations/powerpoint/features/rephrase-slide/services/readRephraseTargets';
import { requestRewrites } from '../../integrations/powerpoint/features/rephrase-slide/services/requestRewrites';
import {
  changedRewrites,
  type ApplyRewritesResult,
  type RephraseReadResult,
  type SlideRewriteCard,
} from '../../integrations/powerpoint/features/rephrase-slide/types';
import type { SlideRewriteDecision } from '../../integrations/powerpoint/features/rephrase-slide/components/SlideRewriteCard';
import type { Channel } from '../../shared/host';
import { parseSlash } from '../slashCommands/parser';
import { REPHRASE_SLASH } from '../slashCommands/localCommands';
import type { ChatMessage } from '../types';

export const REPHRASE_CAPABILITY_ID = 'SC-06';

/** `/rephrase` on its own: no "how" was given, so rewrite the slide clearer and tighter. */
const DEFAULT_SLASH_REQUEST = 'rewrite this slide';

/**
 * The request a PowerPoint rephrase turn works from: a matching phrase, or the words after `/rephrase`.
 * Null when this isn't one; a forced skill (palette, pill, other slash) always wins.
 */
export function rephraseSlideRequest(
  host: Channel | null,
  text: string,
  forcedSkillKey: string | undefined,
  messages: readonly ChatMessage[] = []
): { instruction: string; viaSlash: boolean } | null {
  if (host !== 'powerpoint' || forcedSkillKey) return null;
  const { token, args } = parseSlash(text);
  if (token === REPHRASE_SLASH) return { instruction: args || DEFAULT_SLASH_REQUEST, viaSlash: true };
  const afterDraft = lastReplyIsDraft(messages);
  return matchRephraseSlideIntent(text, afterDraft) ? { instruction: text.trim(), viaSlash: false } : null;
}

/** The newest AiVA reply is a draft or a deck summary, so "make it punchier" means that reply. */
export function lastReplyIsDraft(messages: readonly ChatMessage[]): boolean {
  const last = [...messages].reverse().find((m) => m.role === 'assistant');
  return (last?.actionPills?.length ?? 0) > 0 || last?.deckSummary !== undefined;
}

type Setter = Dispatch<SetStateAction<ChatMessage[]>>;

/** Cards already decided: a fast double-click reaches here twice before the disabled buttons render. */
const decidedCards = new Set<string>();

function readFailureText(read: Exclude<RephraseReadResult, { kind: 'ok' }>): string {
  if (read.kind === 'no_text') return REPHRASE_COPY.NO_TEXT;
  if (read.kind === 'no_slide') return NO_SLIDE_SELECTED_MESSAGE;
  return SLIDE_UNREADABLE_MESSAGE;
}

function readFailureOutcome(read: Exclude<RephraseReadResult, { kind: 'ok' }>) {
  if (read.kind === 'no_text') return { outcome: 'no_match', errorCode: 'no_text' } as const;
  if (read.kind === 'no_slide') return { outcome: 'no_match', errorCode: 'no_slide_selected' } as const;
  return { outcome: 'unsupported_api', errorCode: read.errorCode } as const;
}

export interface RephraseSlideTurnArgs {
  /** What the user typed — shown in their bubble. */
  text: string;
  /** What the model is asked to do; defaults to `text` (differs for `/rephrase …`). */
  instruction?: string;
  hidden: boolean | undefined;
  setMessages: Setter;
  nextMsgId: () => string;
  apiToken: string | null;
  refresh?: RefreshTokenFn;
  trigger?: CapabilityTrigger;
  /** Open spans by card message id, closed when the user decides. */
  spans: Map<string, CapabilitySpan>;
  read?: typeof readRephraseTargets;
  request?: typeof requestRewrites;
}

/** Posts "Reading slide…", then swaps it in place for the card or a plain answer. Never rejects. */
export async function runRephraseSlideTurn(args: RephraseSlideTurnArgs): Promise<void> {
  const { text, hidden, setMessages, nextMsgId, spans, read = readRephraseTargets, request = requestRewrites } = args;
  const now = Date.now();
  const statusId = nextMsgId();
  const user: ChatMessage[] = hidden ? [] : [{ id: nextMsgId(), role: 'user', content: text.trim(), timestamp: now }];
  setMessages((prev) => [...prev, ...user, { id: statusId, role: 'assistant', content: REPHRASE_COPY.READING, timestamp: now }]);
  const settle = (patch: Partial<ChatMessage>) =>
    setMessages((prev) => prev.map((m) => (m.id === statusId ? { ...m, ...patch } : m)));

  const span = startCapability({
    capabilityId: REPHRASE_CAPABILITY_ID,
    trigger: args.trigger ?? 'natural_language',
    facts: { aiUsed: false, wroteToDocument: false },
  });
  const base = { wroteToDocument: false } as const;
  try {
    const targets = await read();
    if (targets.kind !== 'ok') {
      settle({ content: readFailureText(targets) });
      span.complete({ ...base, aiUsed: false, ...readFailureOutcome(targets) });
      return;
    }

    let reply: string;
    try {
      reply = await request({
        message: buildRephrasePrompt(args.instruction ?? text, targets.targets),
        apiToken: args.apiToken,
        refresh: args.refresh,
      });
    } catch (err) {
      settle({ content: err instanceof Error ? err.message : REPHRASE_COPY.REQUEST_FAILED });
      span.complete({ ...base, aiUsed: true, outcome: 'error', errorCode: 'ai_request_failed' });
      return;
    }

    const rewrites = parseRewrites(reply, targets.targets);
    if (rewrites === null) {
      settle({ content: REPHRASE_COPY.UNPARSEABLE });
      span.complete({ ...base, aiUsed: true, outcome: 'error', errorCode: 'unparseable_response' });
      return;
    }
    if (changedRewrites({ rewrites }).length === 0) {
      settle({ content: REPHRASE_COPY.NO_CHANGES });
      span.complete({ ...base, aiUsed: true, outcome: 'success', appliedCount: 0 });
      return;
    }

    const card: SlideRewriteCard = { status: 'pending', shapeIds: targets.shapeIds, rewrites };
    settle({ content: '', slideRewrite: card });
    span.observe({ aiUsed: true, confirmShown: true, shapeCount: targets.targets.length });
    spans.set(statusId, span);
    // Message ids can repeat (a new chat restarts them), so a new card starts undecided.
    decidedCards.delete(statusId);
  } catch (err) {
    logOfficeError(err, 'ppt-rephrase-slide');
    settle({ content: SLIDE_UNREADABLE_MESSAGE });
    span.complete({ ...base, aiUsed: false, outcome: 'error', errorCode: 'unexpected' });
  }
}

export interface DecideSlideRewriteArgs {
  messageId: string;
  card: SlideRewriteCard;
  decision: SlideRewriteDecision;
  setMessages: Setter;
  spans: Map<string, CapabilitySpan>;
  apply?: (card: SlideRewriteCard) => Promise<ApplyRewritesResult>;
}

const STATUS_FOR: Record<ApplyRewritesResult['kind'], SlideRewriteCard['status']> = {
  applied: 'applied',
  stale: 'stale',
  failed: 'failed',
};

/** Apply rewrites / Cancel on a pending card. Writes only on Apply, and only if the slide is unchanged (G-3). */
export async function decideSlideRewrite(args: DecideSlideRewriteArgs): Promise<void> {
  const { messageId, card, decision, setMessages, spans, apply = applySlideRewrites } = args;
  if (card.status !== 'pending' || decidedCards.has(messageId)) return;
  decidedCards.add(messageId);
  const patch = (status: SlideRewriteCard['status']) =>
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId && m.slideRewrite ? { ...m, slideRewrite: { ...m.slideRewrite, status } } : m))
    );
  const span = spans.get(messageId);
  spans.delete(messageId);
  const facts = { aiUsed: true, confirmShown: true } as const;

  if (decision === 'cancel') {
    patch('cancelled');
    span?.complete({ ...facts, outcome: 'cancelled', confirmAccepted: false, wroteToDocument: false });
    return;
  }

  patch('applying');
  const result = await apply(card);
  patch(STATUS_FOR[result.kind]);
  if (result.kind === 'applied') {
    emitToast({ title: rephraseAppliedMessage(result.count), intent: 'success' });
    span?.complete({ ...facts, outcome: 'success', confirmAccepted: true, wroteToDocument: true, appliedCount: result.count });
  } else {
    const errorCode = result.kind === 'stale' ? 'slide_changed' : 'write_failed';
    span?.complete({ ...facts, outcome: 'error', errorCode, confirmAccepted: true, wroteToDocument: false });
  }
}
